import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { db } from '../config/firebase';
import ProductCard from '../components/products/ProductCard';
import SEO from '../components/SEO';
import { Filter, X } from 'lucide-react';

const Products = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    category: searchParams.get('category') || '',
    search: searchParams.get('search') || '',
    minPrice: '',
    maxPrice: '',
    sortBy: 'newest'
  });

  // Update filters when URL search params change
  useEffect(() => {
    const category = searchParams.get('category') || '';
    const search = searchParams.get('search') || '';

    setFilters(prev => ({
      ...prev,
      category,
      search
    }));
  }, [searchParams]);

  useEffect(() => {
    fetchProducts();
  }, [filters]);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      let q = query(collection(db, 'products'));

      // Apply category filter
      if (filters.category) {
        q = query(q, where('category', '==', filters.category));
      }

      const querySnapshot = await getDocs(q);
      let productsData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Apply search filter (client-side)
      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        productsData = productsData.filter(p =>
          p.name.toLowerCase().includes(searchLower) ||
          p.description?.toLowerCase().includes(searchLower)
        );
      }

      // Apply price filters
      if (filters.minPrice) {
        productsData = productsData.filter(p => p.price >= parseInt(filters.minPrice));
      }
      if (filters.maxPrice) {
        productsData = productsData.filter(p => p.price <= parseInt(filters.maxPrice));
      }

      // Apply sorting
      productsData.sort((a, b) => {
        switch (filters.sortBy) {
          case 'price-low':
            return a.price - b.price;
          case 'price-high':
            return b.price - a.price;
          case 'name':
            return a.name.localeCompare(b.name);
          default: // newest
            return new Date(b.createdAt) - new Date(a.createdAt);
        }
      });

      setProducts(productsData);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    if (key === 'category') {
      setSearchParams(prev => {
        if (value) {
          prev.set('category', value);
        } else {
          prev.delete('category');
        }
        return prev;
      });
    }
  };

  const clearFilters = () => {
    setFilters({
      category: '',
      search: '',
      minPrice: '',
      maxPrice: '',
      sortBy: 'newest'
    });
    setSearchParams({});
  };

  const pageTitle = filters.category === 'sunglasses'
    ? 'Kính Mát | Kính mắt Nhân Văn'
    : filters.category === 'nearsighted'
    ? 'Kính Cận | Kính mắt Nhân Văn'
    : 'Tất Cả Sản Phẩm | Kính mắt Nhân Văn';
  const pageDescription = filters.category === 'sunglasses'
    ? 'Khám phá bộ sưu tập kính mát cao cấp của chúng tôi. Tìm kính mát hoàn hảo với bảo vệ UV, thiết kế thời trang và chất liệu chất lượng.'
    : filters.category === 'nearsighted'
    ? 'Khám phá bộ sưu tập kính cận cao cấp của chúng tôi. Tìm kính cận hoàn hảo với bảo vệ UV, thiết kế thời trang và chất liệu chất lượng.'
    : 'Mua sắm tất cả sản phẩm kính mắt tại Kính mắt Nhân Văn. Khám phá kính mát và kính cận cao cấp với bảo vệ UV và thiết kế thời trang.';

  return (
    <>
      <SEO
        title={pageTitle}
        description={pageDescription}
        keywords={`${filters.category || 'eyewear'}, glasses, buy online, UV protection, fashion eyewear`}
      />
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="container mx-auto px-4">
          {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold">
            {filters.category === 'sunglasses' ? 'Kính Mát' : filters.category === 'nearsighted' ? 'Kính Cận' : 'Tất Cả Sản Phẩm'}
          </h1>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="md:hidden flex items-center gap-2 bg-white px-4 py-2 rounded-lg shadow"
          >
            <Filter className="w-5 h-5" />
            Bộ Lọc
          </button>
        </div>

        <div className="flex flex-col md:flex-row gap-8">
          {/* Filters Sidebar */}
          <div className={`${showFilters ? 'block' : 'hidden'} md:block md:w-64 flex-shrink-0`}>
            <div className="bg-white rounded-lg shadow-md p-6 sticky top-24">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold">Bộ Lọc</h2>
                <button
                  onClick={clearFilters}
                  className="text-sm text-primary-600 hover:underline"
                >
                  Xóa Tất Cả
                </button>
              </div>

              {/* Category Filter */}
              <div className="mb-6">
                <h3 className="font-semibold mb-3">Danh Mục</h3>
                <div className="space-y-2">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="category"
                      checked={filters.category === ''}
                      onChange={() => handleFilterChange('category', '')}
                      className="mr-2"
                    />
                    Tất Cả Sản Phẩm
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="category"
                      checked={filters.category === 'sunglasses'}
                      onChange={() => handleFilterChange('category', 'sunglasses')}
                      className="mr-2"
                    />
                    Kính Mát
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="category"
                      checked={filters.category === 'nearsighted'}
                      onChange={() => handleFilterChange('category', 'nearsighted')}
                      className="mr-2"
                    />
                    Kính Cận
                  </label>
                </div>
              </div>

              {/* Price Filter */}
              <div className="mb-6">
                <h3 className="font-semibold mb-3">Khoảng Giá</h3>
                <div className="space-y-2">
                  <input
                    type="number"
                    placeholder="Giá Tối Thiểu"
                    value={filters.minPrice}
                    onChange={(e) => handleFilterChange('minPrice', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                  <input
                    type="number"
                    placeholder="Giá Tối Đa"
                    value={filters.maxPrice}
                    onChange={(e) => handleFilterChange('maxPrice', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
              </div>

              {/* Sort By */}
              <div>
                <h3 className="font-semibold mb-3">Sắp Xếp Theo</h3>
                <select
                  value={filters.sortBy}
                  onChange={(e) => handleFilterChange('sortBy', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="newest">Mới Nhất</option>
                  <option value="price-low">Giá: Thấp Đến Cao</option>
                  <option value="price-high">Giá: Cao Đến Thấp</option>
                  <option value="name">Tên: A Đến Z</option>
                </select>
              </div>
            </div>
          </div>

          {/* Products Grid */}
          <div className="flex-1">
            {loading ? (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
              </div>
            ) : products.length > 0 ? (
              <>
                <p className="text-gray-600 mb-4">{products.length} sản phẩm</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {products.map(product => (
                    <ProductCard key={product.id} product={product} />
                  ))}
                </div>
              </>
            ) : (
              <div className="text-center py-12 bg-white rounded-lg">
                <p className="text-gray-500 text-lg">Không tìm thấy sản phẩm phù hợp.</p>
                <button
                  onClick={clearFilters}
                  className="mt-4 text-primary-600 hover:underline"
                >
                  Xóa bộ lọc
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
      </div>
    </>
  );
};

export default Products;
